import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-511e6b92"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "profile-relationships" }
const _hoisted_2 = { class: "parents-area" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h5", null, "Parents", -1))
const _hoisted_4 = {
  key: 0,
  class: "list-unstyled parents-list"
}
const _hoisted_5 = {
  key: 1,
  class: "alert alert-light alert-xs other-rels-alert"
}
const _hoisted_6 = {
  key: 2,
  class: "no-parents"
}
const _hoisted_7 = {
  key: 3,
  class: "action-row"
}
const _hoisted_8 = { class: "spouses-area" }
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h5", null, "Spouses", -1))
const _hoisted_10 = {
  key: 0,
  class: "list-unstyled spouses-list"
}
const _hoisted_11 = {
  key: 1,
  class: "alert alert-light alert-xs other-rels-alert"
}
const _hoisted_12 = {
  key: 2,
  class: "no-spouses"
}
const _hoisted_13 = {
  key: 3,
  class: "action-row"
}

import { computed, ref } from 'vue'
import { Gender, ViewFamily } from '@/rd/ResearchDataModel'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { FamilyAndChildren, useViewFamilyStore } from '@/rd/ViewFamilyStore'
import { usePersonStore } from '@/rd/PersonStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { ItemPermissions } from '@/gp/GroupAdminModel'
import { CompositeId } from '@/rd/CompositeId'
import { NewProfileRelationship } from './ProfileManager'
import _, { sortBy } from 'lodash'
import ProfileSpouseRelationship from './ProfileSpouseRelationship.vue'
import ProfileParentRelationship from './ProfileParentRelationship.vue'
import ProfileAddRelatedModal from './ProfileAddRelatedModal.vue'
import ProfileSpouseRemoveModal from './ProfileSpouseRemoveModal.vue'
import ProfileChildRemoveModal from './ProfileChildRemoveModal.vue'
import ProfileAddParentsModal from './ProfileAddParentsModal.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'ProfileRelationships',
  props: {
    personId: {},
    ownerOnly: { type: Boolean }
  },
  emits: [
  'reload',
],
  setup(__props: any, { emit }) {

const props = __props;





const viewPersonStore = useViewPersonStore()
const viewFamilyStore = useViewFamilyStore()
const personStore = usePersonStore()
const dataGroupStore = useDataGroupStore()

const personGroupIdRef = computed(() => CompositeId.getGroupId(props.personId))
const personRef = computed(() => personStore.getAsyncPerson(props.personId)?.data)
const viewPersonRef = computed(() => viewPersonStore.getLoadedPerson(props.personId))
const allSpouseViewFamiliesRef = computed(() => {
  const vpId = viewPersonRef.value?.id // use view person id to get related families
  if (!vpId)
    return []

  const spouseViewFamilies = viewFamilyStore.getLoadedSpouseFamilies(vpId).spouseFamilies
  const spouseFamiliesAndChildren: FamilyAndChildren[] = []
  for (const sf of spouseViewFamilies) {
    const children = viewFamilyStore.getLoadedChildren(sf.id!).children
    spouseFamiliesAndChildren.push({
      family: sf,
      childIds: children.map(ch => ch.id!),
    })
  }
  return sortBy(spouseFamiliesAndChildren, f => !isOwnerFamily(f)) // profile families first
})
const ownerSpouseViewFamiliesRef = computed(() => allSpouseViewFamiliesRef.value.filter(isOwnerFamily))
const visibleSpouseViewFamiliesRef = computed(() => 
  props.ownerOnly ? ownerSpouseViewFamiliesRef.value : allSpouseViewFamiliesRef.value)
const hasOtherSpouseFamiliesRef = computed(() => 
  allSpouseViewFamiliesRef.value.length > ownerSpouseViewFamiliesRef.value.length)

const allParentViewFamiliesRef = computed(() => {
  const vpId = viewPersonRef.value?.id // use view person id to get related families
  if (!vpId)
    return []

  const parentViewFamilies = viewPersonStore.getLoadedParentFamilies(vpId).parentFamilies
  const parentFamAndChildren: FamilyAndChildren[] = []
  for (const pf of parentViewFamilies) {
    const children = viewFamilyStore.getLoadedChildren(pf.id!).children
    parentFamAndChildren.push({
      family: pf,
      childIds: children.map(ch => ch.id!),
    })
  }
  return sortBy(parentFamAndChildren, f => !isOwnerFamily(f)) // owned families first
})
const ownerParentViewFamiliesRef = computed(() => allParentViewFamiliesRef.value.filter(isOwnerFamily))
const visibleParentViewFamiliesRef = computed(() => 
  props.ownerOnly ? ownerParentViewFamiliesRef.value : allParentViewFamiliesRef.value)
const hasOtherParentFamiliesRef = computed(() => 
  allParentViewFamiliesRef.value.length > ownerParentViewFamiliesRef.value.length)

const permissionsRef = computed(() => dataGroupStore.getAsyncPermissions(personGroupIdRef.value)?.data ?? ItemPermissions.None)
const canEditRef = computed(() => (permissionsRef.value & ItemPermissions.Modify) > 0)

const expandedFamilyIdRef = ref<string>()
const addRelatedModal = ref<InstanceType<typeof ProfileAddRelatedModal>>()
const removeSpouseModalRef = ref<InstanceType<typeof ProfileSpouseRemoveModal>>()
const removeChildModalRef = ref<InstanceType<typeof ProfileChildRemoveModal>>()
const addParentsModalRef = ref<InstanceType<typeof ProfileAddParentsModal>>()

function isOwnerFamily(f: FamilyAndChildren) {
  return f.family.matchIds.some(id => CompositeId.hasGroupId(id, personGroupIdRef.value!))
}

function addSpouse() {
  const personGender = personRef.value?.displayProperties.gender
  if (!personGender) {
    alert('Please enter the gender for this profile before adding a spouse.')
    return
  }

  const spouseRel = personGender == Gender.Male
    ? NewProfileRelationship.Wife
    : NewProfileRelationship.Husband

  addRelatedModal.value?.open(spouseRel, props.personId)
}

function onAddChild(spouseFamily: ViewFamily) {
  addRelatedModal.value?.open(NewProfileRelationship.Son, spouseFamily.id!)
}

function onRemoveSpouse(spouseFamily: FamilyAndChildren) {
  removeSpouseModalRef.value?.open(props.personId, spouseFamily)
}

function onAddSibling(parentFamily: ViewFamily) {
  addRelatedModal.value?.open(NewProfileRelationship.Son, parentFamily.id!)
}

function addParents() {
  addParentsModalRef.value?.open(props.personId)
}

function onChangeParents() {
  //
}

function onRemoveParents(parentFamily: ViewFamily) {
  removeChildModalRef.value?.open(props.personId, parentFamily, true)
}

// list matches
// match/unmatch
// show person sets and owners
// don't confuse matches and profile relationships (match placeholder to primary)

// show view spouse?
// show direct spouse relationships (defined via placeholder match by this or the other profile)
// show family details somehow?
// add/remove spouses
// ISSUE: spouse family is replicated in child profiles, so all copies would need to be "disputed"

// show direct parent relationships (defined via placeholder match in this profile)
// add/remove parent relationships


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("section", _hoisted_2, [
      _hoisted_3,
      (visibleParentViewFamiliesRef.value.length)
        ? (_openBlock(), _createElementBlock("ul", _hoisted_4, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleParentViewFamiliesRef.value, (pf) => {
              return (_openBlock(), _createElementBlock("li", {
                key: pf.family.id
              }, [
                _createVNode(ProfileParentRelationship, {
                  "child-id": props.personId,
                  "parent-family": pf,
                  "owner-only": props.ownerOnly,
                  onAddSibling: ($event: any) => (onAddSibling(pf.family)),
                  onChange: onChangeParents,
                  onRemove: onRemoveParents
                }, null, 8, ["child-id", "parent-family", "owner-only", "onAddSibling"])
              ]))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      (props.ownerOnly && hasOtherParentFamiliesRef.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            (visibleParentViewFamiliesRef.value.length)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createTextVNode(" Another set of parents are defined in a matching profile. ")
                ], 64))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createTextVNode(" Parents are defined in a matching profile. ")
                ], 64))
          ]))
        : _createCommentVNode("", true),
      (!visibleParentViewFamiliesRef.value.length && !canEditRef.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, " None "))
        : _createCommentVNode("", true),
      (canEditRef.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-inline btn-link action",
              onClick: addParents
            }, " Add parents ")
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("section", _hoisted_8, [
      _hoisted_9,
      (visibleSpouseViewFamiliesRef.value.length)
        ? (_openBlock(), _createElementBlock("ul", _hoisted_10, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleSpouseViewFamiliesRef.value, (sf) => {
              return (_openBlock(), _createElementBlock("li", {
                key: sf.family.id
              }, [
                _createVNode(ProfileSpouseRelationship, {
                  "person-id": props.personId,
                  "spouse-family": sf,
                  "owner-only": props.ownerOnly,
                  "expanded-id": expandedFamilyIdRef.value,
                  "onUpdate:expandedId": _cache[0] || (_cache[0] = ($event: any) => ((expandedFamilyIdRef).value = $event)),
                  onAddChild: ($event: any) => (onAddChild(sf.family)),
                  onRemove: ($event: any) => (onRemoveSpouse(sf))
                }, null, 8, ["person-id", "spouse-family", "owner-only", "expanded-id", "onAddChild", "onRemove"])
              ]))
            }), 128))
          ]))
        : _createCommentVNode("", true),
      (props.ownerOnly && hasOtherSpouseFamiliesRef.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
            (visibleSpouseViewFamiliesRef.value.length)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createTextVNode(" Another spouse is defined in a matching or related profile. ")
                ], 64))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createTextVNode(" A spouse is defined in a matching or related profile. ")
                ], 64))
          ]))
        : _createCommentVNode("", true),
      (!visibleSpouseViewFamiliesRef.value.length && !canEditRef.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_12, " None "))
        : _createCommentVNode("", true),
      (canEditRef.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-inline btn-link action",
              onClick: addSpouse
            }, " Add a spouse ")
          ]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(ProfileAddRelatedModal, {
      ref_key: "addRelatedModal",
      ref: addRelatedModal,
      onAdded: _cache[1] || (_cache[1] = ($event: any) => (emit('reload')))
    }, null, 512),
    _createVNode(ProfileSpouseRemoveModal, {
      ref_key: "removeSpouseModalRef",
      ref: removeSpouseModalRef
    }, null, 512),
    _createVNode(ProfileChildRemoveModal, {
      ref_key: "removeChildModalRef",
      ref: removeChildModalRef,
      onRemoved: _cache[2] || (_cache[2] = ($event: any) => (emit('reload')))
    }, null, 512),
    _createVNode(ProfileAddParentsModal, {
      ref_key: "addParentsModalRef",
      ref: addParentsModalRef,
      onAdded: _cache[3] || (_cache[3] = ($event: any) => (emit('reload')))
    }, null, 512)
  ]))
}
}

})